import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, BarChart3, CalendarCheck, FileText, Trophy, Users } from "lucide-react";

const FEATURES = [
  { icon: BarChart3, color: "bg-blue-700", soft: "bg-blue-50", title: "Desempenho acadêmico", text: "Acompanhe o desempenho e identifique oportunidades de evolução." },
  { icon: CalendarCheck, color: "bg-green-600", soft: "bg-green-50", title: "Frequência", text: "Monitore a frequência, identifique ausências e fortaleça o vínculo com o aluno." },
  { icon: FileText, color: "bg-orange-500", soft: "bg-orange-50", title: "Disciplinar", text: "Registre ocorrências, acompanhe situações e mantenha a comunicação organizada." },
  { icon: Trophy, color: "bg-purple-600", soft: "bg-purple-50", title: "Excelência acadêmica", text: "Reconheça conquistas, incentive o desenvolvimento e valorize o protagonismo do aluno." },
  { icon: Users, color: "bg-blue-500", soft: "bg-sky-50", title: "Gestão integrada", text: "Centralize informações e promova uma gestão escolar mais eficiente e colaborativa." },
];

const NAV = [["inicio", "Início"], ["funcionalidades", "Funcionalidades"], ["para-escolas", "Para escolas"]];
const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

const Header = ({ onLogin }) => (
  <header className="sticky top-0 z-30 border-b border-slate-100 bg-white/90 backdrop-blur-md">
    <div className="mx-auto flex max-w-[1500px] items-center justify-between gap-4 px-4 py-3 sm:px-10">
      <img src="/landing/logo-header.png" alt="Acompanha — Plataforma de Acompanhamento Escolar" className="h-8 w-auto min-w-0 shrink sm:h-14" data-testid="landing-logo" />
      <nav className="hidden items-center gap-10 md:flex">
        {NAV.map(([id, label], i) => (
          <button key={id} onClick={() => go(id)} className={`pb-1 text-[15px] font-medium text-slate-800 transition-colors hover:text-blue-700 ${i === 0 ? "border-b-[3px] border-blue-600 font-semibold" : ""}`} data-testid={`landing-nav-${id}`}>{label}</button>
        ))}
      </nav>
      <button onClick={onLogin} className="flex shrink-0 items-center gap-2 whitespace-nowrap rounded-xl bg-orange-500 px-3 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-500/30 transition-transform hover:-translate-y-0.5 hover:bg-orange-600 sm:px-7 sm:py-3 sm:text-base" data-testid="landing-access-btn">
        <span className="sm:hidden">Acessar</span><span className="hidden sm:inline">Acessar plataforma</span> <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  </header>
);

const Hero = () => (
  <section id="inicio" className="relative overflow-hidden bg-gradient-to-r from-sky-50 via-white to-white">
    <div className="pointer-events-none absolute -left-24 top-0 hidden h-[380px] w-[200px] rotate-[25deg] rounded-[60px] bg-blue-500/80 lg:block" />
    <div className="pointer-events-none absolute -left-20 bottom-[-120px] h-[260px] w-[200px] rotate-[25deg] rounded-[60px] border-[3px] border-orange-400" />
    <div className="relative grid lg:grid-cols-[minmax(0,560px)_1fr]">
      <div className="relative z-10 px-6 py-14 sm:px-16 lg:py-20 lg:pl-28">
        <h1 className="font-display text-6xl font-black leading-[0.92] tracking-tight text-[#0b2a6b] sm:text-7xl lg:text-[88px]" data-testid="landing-title">
          Cada<br /><span className="text-blue-600">presença</span><br />conta<span className="text-orange-500">.</span>
        </h1>
        <p className="mt-7 text-xl leading-snug text-slate-600 sm:text-2xl">Mais cuidado com o aluno.<br />Mais organização para a escola.</p>
        <button onClick={() => go("funcionalidades")} className="mt-9 flex items-center gap-3 rounded-2xl bg-orange-500 px-8 py-4 text-lg font-bold uppercase tracking-wide text-white shadow-xl shadow-orange-500/30 transition-transform hover:-translate-y-0.5 hover:bg-orange-600" data-testid="landing-know-btn">
          Conheça a plataforma <ArrowRight className="h-5 w-5" />
        </button>
      </div>
      <div className="relative min-h-[320px] lg:min-h-[536px]">
        <img src="/landing/hero.jpg" alt="Professora e estudantes usando a plataforma em um tablet" className="absolute inset-0 h-full w-full object-cover object-left" data-testid="landing-hero-img" />
        <div className="absolute inset-y-0 left-0 hidden w-24 bg-gradient-to-r from-white to-transparent lg:block" />
      </div>
    </div>
  </section>
);

const Features = () => (
  <section id="funcionalidades" className="scroll-mt-24 bg-white px-4 py-10 sm:px-10">
    <div className="mx-auto grid max-w-[1500px] gap-6 rounded-3xl bg-slate-50/70 p-6 sm:grid-cols-2 lg:grid-cols-5 lg:gap-0 lg:divide-x lg:divide-slate-200">
      {FEATURES.map((f, i) => (
        <article key={f.title} className="group px-2 lg:px-7" style={{ animation: `fadeUp .6s ease ${i * 0.08}s both` }} data-testid={`landing-feature-${i}`}>
          <div className="flex items-start justify-between">
            <span className={`grid h-16 w-16 place-items-center rounded-full ${f.color} text-white shadow-md transition-transform group-hover:scale-105`}><f.icon className="h-8 w-8" /></span>
            <span className={`grid h-20 w-24 place-items-center rounded-2xl ${f.soft}`}><f.icon className="h-9 w-9 opacity-40" /></span>
          </div>
          <h3 className="mt-3 font-display text-xl font-bold leading-tight text-[#0b2a6b]">{f.title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.text}</p>
        </article>
      ))}
    </div>
  </section>
);

const ForSchools = ({ onLogin }) => (
  <section id="para-escolas" className="scroll-mt-24 bg-[#0b2a6b] px-6 py-16 text-white sm:px-16">
    <div className="mx-auto flex max-w-[1500px] flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
      <div className="max-w-2xl">
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-orange-400">Para escolas</p>
        <h2 className="mt-3 font-display text-3xl font-black sm:text-4xl">Mais cuidado com o aluno. Mais organização para a escola.</h2>
        <p className="mt-4 text-base text-blue-100">Desempenho acadêmico, frequência, disciplinar, excelência acadêmica e gestão integrada em uma só plataforma de acompanhamento escolar.</p>
      </div>
      <button onClick={onLogin} className="flex w-fit items-center gap-2 rounded-xl bg-orange-500 px-7 py-4 text-base font-bold text-white shadow-lg transition-transform hover:-translate-y-0.5 hover:bg-orange-600" data-testid="landing-schools-access-btn">
        Acessar plataforma <ArrowRight className="h-5 w-5" />
      </button>
    </div>
  </section>
);

export default function Landing() {
  const navigate = useNavigate();
  const onLogin = () => navigate("/login");
  return (
    <div className="min-h-screen bg-white font-body" data-testid="landing-page">
      <style>{"@keyframes fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}}"}</style>
      <Header onLogin={onLogin} />
      <Hero />
      <Features />
      <ForSchools onLogin={onLogin} />
      <footer className="bg-white px-6 py-6 text-center text-xs text-slate-400">ACOMPANHA — Plataforma de Acompanhamento Escolar</footer>
    </div>
  );
}
